/**
 * Phased Project Structures — grouping, totals, and commitment helpers.
 *
 * Structures (`nodeSets`) are grouped into ordered `ProposalPhase`s. The client
 * signs the proposal for the committed/offered phase(s) only; structures in
 * `future` phases are excluded from signing totals via the existing
 * `structureSelections` map. Later phases commit through builder-mode change
 * orders, so the contract-value ledger / sales orders / QB invoicing all keep
 * working unchanged.
 */

import { calculateTotals } from './proposalCalculations';
import { computeBondingAdjustment, effectiveBondingPercent } from './proposalBonding';
import type {
  ProposalBuilderData,
  ProposalNodeSet,
  ProposalPhase,
  ProposalPhaseStatus,
} from '../types';

/** True when the proposal has Phased Project mode on with at least one phase defined. */
export function isPhasedProposal(
  proposal: Pick<ProposalBuilderData, 'phasingEnabled' | 'phases'> | null | undefined,
): boolean {
  return !!proposal?.phasingEnabled && (proposal.phases?.length || 0) > 0;
}

/** Phases sorted by `order` (stable for equal orders). */
export function getOrderedPhases(
  proposal: Pick<ProposalBuilderData, 'phases'>,
): ProposalPhase[] {
  return [...(proposal.phases || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function getProposalPhaseById(
  proposal: Pick<ProposalBuilderData, 'phases'>,
  phaseId: string | undefined | null,
): ProposalPhase | null {
  if (!phaseId) return null;
  return (proposal.phases || []).find(p => p.id === phaseId) || null;
}

/** First phase in order — where unassigned structures land. */
export function getBasePhase(
  proposal: Pick<ProposalBuilderData, 'phases'>,
): ProposalPhase | null {
  return getOrderedPhases(proposal)[0] || null;
}

/** Effective phase id for a structure: its `phaseId` when valid, else the base phase. */
export function getNodeSetPhaseId(
  proposal: Pick<ProposalBuilderData, 'phases'>,
  nodeSet: Pick<ProposalNodeSet, 'phaseId'>,
): string | null {
  if (nodeSet.phaseId && (proposal.phases || []).some(p => p.id === nodeSet.phaseId)) {
    return nodeSet.phaseId;
  }
  return getBasePhase(proposal)?.id || null;
}

/** Enabled structures assigned to a phase (unassigned structures fall to the base phase). */
export function getPhaseNodeSets(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets'>,
  phaseId: string,
): ProposalNodeSet[] {
  return (proposal.nodeSets || []).filter(
    ns => ns.enabled !== false && getNodeSetPhaseId(proposal, ns) === phaseId,
  );
}

export interface PhaseGroup {
  phase: ProposalPhase;
  nodeSets: ProposalNodeSet[];
}

/** Ordered phases, each with its enabled structures. */
export function groupNodeSetsByPhase(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets'>,
): PhaseGroup[] {
  return getOrderedPhases(proposal).map(phase => ({
    phase,
    nodeSets: getPhaseNodeSets(proposal, phase.id),
  }));
}

/**
 * structureSelections map derived from phase membership: enabled structures in
 * phases whose status is in `includeStatuses` → true, everything else → false.
 * Feeding this into the existing selection-aware pricing keeps grand totals,
 * signed snapshots, and sales-order lines scoped to committed work.
 */
export function buildPhaseStructureSelections(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets'>,
  includeStatuses: ProposalPhaseStatus[] = ['committed', 'offered'],
): Record<string, boolean> {
  const selections: Record<string, boolean> = {};
  (proposal.nodeSets || []).forEach(ns => {
    if (ns.enabled === false) return;
    const phase = getProposalPhaseById(proposal, getNodeSetPhaseId(proposal, ns));
    selections[ns.id] = phase ? includeStatuses.includes(phase.status) : true;
  });
  return selections;
}

/** Ids of enabled structures in committed phases. */
export function getCommittedStructureIds(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets'>,
): string[] {
  const selections = buildPhaseStructureSelections(proposal, ['committed']);
  return Object.keys(selections).filter(id => selections[id]);
}

export function getPhasesByStatus(
  proposal: Pick<ProposalBuilderData, 'phases'>,
  status: ProposalPhaseStatus,
): ProposalPhase[] {
  return getOrderedPhases(proposal).filter(p => p.status === status);
}

export function hasCommittedPhases(proposal: Pick<ProposalBuilderData, 'phases'>): boolean {
  return (proposal.phases || []).some(p => p.status === 'committed');
}

/**
 * Billable amount for committing a phase — matches `computeChangeOrderDelta`
 * for a builder-mode CO carrying this phase's structures, so what the client
 * sees is exactly what posts to the ledger.
 */
export function computePhaseCommitAmount(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets' | 'profitMargin'> &
    Partial<Pick<ProposalBuilderData, 'globalCosts'>>,
  phaseId: string,
): number {
  const nodeSets = getPhaseNodeSets(proposal, phaseId);
  if (nodeSets.length === 0) return 0;
  const base = Math.round(calculateTotals(nodeSets, proposal.profitMargin || 0).billableTotal);
  // Bonding pass-through rides on the phase CO exactly as it does on the signed proposal.
  return base + computeBondingAdjustment(base, effectiveBondingPercent(proposal.globalCosts));
}

/**
 * Validation problems, empty when the phase setup is signable:
 * - every enabled structure maps to an existing phase (or the base phase);
 * - the base phase has at least one structure;
 * - at least one phase is committed or offered (something to sign).
 */
export function validateProposalPhases(
  proposal: Pick<ProposalBuilderData, 'phases' | 'nodeSets' | 'phasingEnabled'>,
): string[] {
  const problems: string[] = [];
  if (!proposal.phasingEnabled) return problems;
  const phases = getOrderedPhases(proposal);
  if (phases.length === 0) {
    problems.push('Phasing is enabled but no phases are defined.');
    return problems;
  }
  const base = phases[0];
  if (getPhaseNodeSets(proposal, base.id).length === 0) {
    problems.push(`The initial phase ("${base.name}") has no structures assigned.`);
  }
  if (!phases.some(p => p.status === 'committed' || p.status === 'offered')) {
    problems.push('No phase is offered or committed — the client would have nothing to sign.');
  }
  const seen = new Set<string>();
  phases.forEach(p => {
    if (seen.has(p.id)) problems.push(`Duplicate phase id "${p.id}".`);
    seen.add(p.id);
  });
  return problems;
}

let phaseIdCounter = 0;
function generatePhaseId(): string {
  phaseIdCounter += 1;
  return `phase-${Date.now().toString(36)}-${phaseIdCounter}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createProposalPhase(order: number, name?: string): ProposalPhase {
  return {
    id: generatePhaseId(),
    name: name || `Phase ${order + 1}`,
    order,
    // The first phase is what the client signs initially; later phases start future.
    status: order === 0 ? 'offered' : 'future',
  };
}

/** Starter set when a proposal turns phasing on: Phase 1 (offered) + Phase 2 (future). */
export function buildDefaultPhases(): ProposalPhase[] {
  return [createProposalPhase(0), createProposalPhase(1)];
}

/** Pure helper: mark a phase committed with signature metadata. */
export function markPhaseCommitted(
  phases: ProposalPhase[],
  phaseId: string,
  meta: {
    committedBy: string;
    committedAt?: Date;
    signature?: ProposalPhase['signature'];
    changeOrderId?: string;
  },
): ProposalPhase[] {
  return phases.map(p =>
    p.id === phaseId
      ? {
          ...p,
          status: 'committed' as const,
          committedAt: meta.committedAt || new Date(),
          committedBy: meta.committedBy,
          ...(meta.signature ? { signature: meta.signature } : {}),
          ...(meta.changeOrderId ? { changeOrderId: meta.changeOrderId } : {}),
        }
      : p,
  );
}

/** Mark all currently-offered phases committed (initial proposal signing). */
export function commitOfferedPhases(
  phases: ProposalPhase[],
  meta: { committedBy: string; committedAt?: Date; signature?: ProposalPhase['signature'] },
): ProposalPhase[] {
  return phases.map(p =>
    p.status === 'offered'
      ? {
          ...p,
          status: 'committed' as const,
          committedAt: meta.committedAt || new Date(),
          committedBy: meta.committedBy,
          ...(meta.signature ? { signature: meta.signature } : {}),
        }
      : p,
  );
}

export function getPhaseStatusLabel(status: ProposalPhaseStatus): string {
  switch (status) {
    case 'committed':
      return 'Committed';
    case 'offered':
      return 'Ready to commit';
    default:
      return 'Future phase';
  }
}
